/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { ModelProperty } from '../engine/model-property';
import { ExportUtils } from './export-utils';
import { DataModel } from '../engine/data-model';
import { ngxCsv } from 'ngx-csv';
import { Cell } from './model/cell';
import { ColumnGroupManager } from '../manager/table-manager/column-group-manager';
import { ColumnManager } from '../manager/table-manager/column-manager';

const logger = getInfCLogger('CsvExportTool');

export class CsvExportTool {
  public model: DataModel;
  public author: string;
  public title: string;
  public columns: ModelProperty[];

  constructor(
    model: DataModel,
    columns: ModelProperty[],
    author: string,
    title: string,
  ) {
    this.model = model;
    this.author = author;
    this.columns = columns;
    this.title = title;
    this.checkColumns();
  }

  public export(
    data: any[],
    directDownload = true,
    displayColumnsGroups = false,
  ): { header: string[]; body: any } {
    const options = {
      fieldSeparator: ',',
      decimalseparator: ',',
      showLabels: true,
      showTitle: false,
      useBom: false,
      noDownload: true,
      headers: ExportUtils.generateHeader(this.columns),
    };
    const body = this.generateCsvBody(this.columns, data);
    const obj = <any>new ngxCsv(body, this.title, options);
    const csv = obj.csv;
    obj.csv = 'sep=,\n';
    if (displayColumnsGroups) {
      obj.csv += this.getColumnsGroupFormatted() + '\n';
    }
    obj.csv += csv;
    if (directDownload) {
      this.download(obj.csv, this.title + '.csv');
    } else {
      return { header: options.headers, body: obj.csv };
    }
    return undefined;
  }

  /**
   * Generate CSV Body (row by row generation with data display conversion)
   * @param columns List of Data Model Property
   * @param data list of row
   */
  protected generateCsvBody(columns: ModelProperty[], data: any[]): Cell[][] {
    const rows: Cell[][] = [];
    for (const d of data) {
      rows.push(ExportUtils.convertDataToRow(d, columns, true));
    }
    return rows;
  }

  public download(csv: string, filename: string) {
    const elem = window.document.createElement('a');
    try {
      elem.href = 'data:text/csv;base64,77u/' + btoa(csv);
    } catch (e) {
      for (let i = 0; i < csv.length; i++) {
        if (csv[i].charCodeAt(0) > 255) {
          console.warn(
            'found character ' +
              csv[i].charCodeAt(0) +
              ' "' +
              csv[i] +
              '" at position ' +
              i,
          );
        }
        logger.error('Download failed', e);
        // todo send message to the user
      }
    }
    elem.download = filename;
    document.body.appendChild(elem);
    elem.click();
    document.body.removeChild(elem);
  }

  protected checkColumns() {
    const columns: ModelProperty[] = [];
    this.columns.forEach((c) => {
      if (c.showOnExport) {
        if (c.replaceOnExport) {
          const replaceColumn = this.model.find(c.replaceOnExport);
          if (replaceColumn) {
            columns.push(replaceColumn);
          } else {
            columns.push(c);
          }
        } else {
          columns.push(c);
        }
      }
    });
    this.columns = columns;
  }

  protected getColumnsGroupFormatted() {
    const groupManager = new ColumnGroupManager();
    this.columns.forEach((c) => groupManager.add(new ColumnManager(c)));
    let s = '';
    for (const gr of groupManager.columnGroups) {
      s += gr.title + gr.columns.join(',');
    }
    return s;
  }
}
